"use client"

import { useState } from "react"
import type { PromptType } from "@/schema/prompt"
import { Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { RefreshCcw, ScrollText } from "lucide-react"

import { isAllBooksId } from "@/lib/all-books"
import { useCreatePromptRun } from "@/hooks/api/prompts"
import { useBookId } from "@/hooks/use-book-id"
import { ConfirmDialog } from "@/components/dialogs"
import { PartnerFeatureGate } from "@/components/partners/partner-feature-gate"

interface PromptButtonsProps {
  className?: string
  size?: "xs" | "sm"
  promptType: PromptType
  recordId: string
  bookId?: string
  meetingType?: string
  isPromptRunInProgress?: boolean
}

const PROMPT_REGENERATE_COPY: Record<
  PromptType,
  {
    title: string
    description: string
    confirmText: string
  }
> = {
  "Prompt::MeetingQuickSummary": {
    title: "Regenerate meeting summary?",
    description: "This will rerun meeting summary generation for this meeting and replace the current summary output.",
    confirmText: "Regenerate summary",
  },
  "Prompt::MeetingPrep": {
    title: "Regenerate meeting prep?",
    description: "This will rerun meeting preparation for this meeting and replace the current prep output.",
    confirmText: "Regenerate prep",
  },
  "Prompt::MeetingNote": {
    title: "Regenerate meeting notes?",
    description: "This will rerun meeting note generation for this meeting and replace the current notes output.",
    confirmText: "Regenerate notes",
  },
  "Prompt::MeetingTasks": {
    title: "Regenerate meeting tasks?",
    description: "This will rerun meeting task generation for this meeting and update the generated task output.",
    confirmText: "Regenerate tasks",
  },
  "Prompt::HouseholdSummary": {
    title: "Regenerate household summary?",
    description: "This will rerun household summary generation and replace the current summary output.",
    confirmText: "Regenerate summary",
  },
}

function PromptButtonsContent({
  className,
  size = "xs",
  promptType,
  recordId,
  bookId,
  meetingType,
  isPromptRunInProgress = false,
}: PromptButtonsProps) {
  const routeBookId = useBookId()
  const createPromptRun = useCreatePromptRun()
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
  const buttonSize = size === "sm" ? "iconSm" : "iconXs"
  const iconSize = size === "sm" ? "size-4!" : "size-3.5!"

  const isMeetingPrompt = promptType.startsWith("Prompt::Meeting")
  const showSettingsButton = promptType !== "Prompt::MeetingQuickSummary"
  const slug = promptType
    .split("::")[1]
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
  const params = new URLSearchParams()
  const settingsBookId = bookId ?? routeBookId
  if (settingsBookId && !isAllBooksId(settingsBookId)) params.set("bookId", settingsBookId)
  if (meetingType) params.set("meetingType", meetingType)
  const basePath = isMeetingPrompt ? "/settings/prompts/meeting-prompts" : `/settings/prompts/${slug}`
  const hash = isMeetingPrompt ? `#${slug}` : ""
  const settingsUrl = `${basePath}${params.size ? `?${params}` : ""}${hash}`

  const handleTextInput = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(settingsUrl, "_blank", "noopener,noreferrer")
  }

  const handleSync = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isPromptRunInProgress || createPromptRun.isPending) return
    setShowRegenerateConfirm(true)
  }

  const confirmRegenerate = () => {
    setShowRegenerateConfirm(false)
    createPromptRun.mutate({ promptableId: recordId, promptType })
  }

  const cancelRegenerate = () => {
    setShowRegenerateConfirm(false)
  }

  const isRegenerateDisabled = isPromptRunInProgress || createPromptRun.isPending
  const regenerateCopy = PROMPT_REGENERATE_COPY[promptType]

  return (
    <div
      className={cn(
        "bg-sand-100 border-sand-300 flex items-center gap-0.5 rounded-lg border p-px",
        size === "sm" && "p-px",
        className
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      data-prompt-type={promptType}
    >
      <ConfirmDialog
        open={showRegenerateConfirm}
        onOpenChange={setShowRegenerateConfirm}
        title={regenerateCopy.title}
        description={regenerateCopy.description}
        dismiss={"explicit"}
        cancel={{ onClick: cancelRegenerate }}
        action={{ label: regenerateCopy.confirmText, onClick: confirmRegenerate }}
      />
      {showSettingsButton && (
        <Button
          variant="ghost"
          size={buttonSize}
          onClick={handleTextInput}
          className={cn("text-sand-600 hover:text-sand-900 hover:bg-sand-300", size === "sm" && "size-7!")}
        >
          <ScrollText className={iconSize} />
        </Button>
      )}
      <Button
        variant="ghost"
        size={buttonSize}
        onClick={handleSync}
        disabled={isRegenerateDisabled}
        className={cn("text-sand-600 hover:text-sand-900 hover:bg-sand-300", size === "sm" && "size-7!")}
      >
        <RefreshCcw className={iconSize} />
      </Button>
    </div>
  )
}

export function PromptButtons(props: React.ComponentProps<typeof PromptButtonsContent>) {
  return (
    <PartnerFeatureGate feature="prompts">
      <PromptButtonsContent {...props} />
    </PartnerFeatureGate>
  )
}
